import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FIRMA, LOGOS } from "@/lib/constants";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (mail, pass) => {
    setError("");
    setLoading(true);
    const res = await login(mail.trim(), pass);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-[#F8FAFC]" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 xl:p-14">
        <div>
          <div className="flex items-center gap-4">
            <img src={LOGOS.governance} alt="Governance Hub" className="h-14 w-14 rounded-xl object-cover" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Governance Hub</p>
              <p className="text-lg font-semibold tracking-tight">Multi-progetto · Multi-area</p>
            </div>
          </div>
          <h1 className="mt-10 text-5xl font-bold tracking-tight leading-[1.05]">
            Tre aree,
            <span className="block text-slate-400">una sola verità di progetto.</span>
          </h1>
          <p className="mt-7 max-w-md text-slate-300 leading-relaxed">
            Stefano Critelli aggiorna i propri task, il Dev Majorbit consolida le percentuali, il Tester
            valida con OK o KO e Infocamere vede l'avanzamento ufficiale e approva le milestone.
          </p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { logo: LOGOS.fornitore, t: "Stefano Critelli", d: "Esegue e dichiara" },
              { logo: LOGOS.cliente, t: "Majorbit", d: "PM · Dev · Tester" },
              { logo: LOGOS.cliente_finale, t: "Infocamere", d: "Verifica e approva" },
            ].map(({ logo, t, d }) => (
              <div key={t} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <img src={logo} alt={t} className="h-10 w-10 rounded-lg object-cover bg-white" />
                <p className="mt-3 font-semibold text-sm">{t}</p>
                <p className="text-xs text-slate-400">{d}</p>
              </div>
            ))}
          </div>
          <p className="pt-5 border-t border-white/10 font-mono text-xs text-slate-500">{FIRMA}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <img src={LOGOS.governance} alt="Governance Hub" className="lg:hidden h-12 w-12 rounded-xl mb-6" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Accesso riservato</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Entra nella tua area</h2>
          <p className="mt-2 text-sm text-slate-600">Le credenziali determinano cosa vedi e cosa puoi aggiornare.</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              doLogin(email, password);
            }}
            className="mt-7 space-y-4"
            data-testid="login-form"
          >
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input id="email" type="email" required autoComplete="email" value={email}
                     onChange={(e) => setEmail(e.target.value)} placeholder="nome@rigenera.it"
                     className="mt-1.5 h-11 bg-white" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                     className="mt-1.5 h-11 bg-white" data-testid="login-password-input" />
            </div>

            {error && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
                 data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" disabled={loading}
                    className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 hover:text-white rounded-lg font-semibold transition-colors"
                    data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>

          <div className="mt-8 border-t border-slate-200 pt-6">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              Area riservata
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              L'accesso è consentito solo ai profili autorizzati. I contenuti del progetto, l'analisi
              funzionale e l'analisi tecnica sono informazioni riservate: ogni accesso e ogni modifica
              vengono registrati. Se non hai credenziali, richiedile all'amministratore.
            </p>
          </div>

          <p className="mt-7 font-mono text-xs text-slate-400 lg:hidden">{FIRMA}</p>
        </div>
      </div>
    </div>
  );
}
